import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Building2, Users, ListChecks } from "lucide-react";

export default function SignUpOrg() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/signup", {
        org_name: orgName,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_password: adminPassword,
      });
      setUser(data.user);
      toast.success(`Welcome to Audentra, ${data.org.name}`, {
        description: `Your workspace '${data.org.slug}' is ready.`,
      });
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Could not create workspace", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <Link to="/" className="inline-flex items-center mb-8" data-testid="signup-logo">
          <img
            src="/audentra-logo.png"
            alt="Audentra"
            className="h-12 w-auto"
          />
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold border border-teal-100">
              <Building2 className="h-3.5 w-3.5" /> Create a workspace
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-4">
              Set up Audentra for your care organisation
            </h1>
            <p className="text-slate-600 mt-2 max-w-xl">
              You&apos;ll be the first manager of the workspace. Invite your team and staff once
              you&apos;re in. No credit card required for the 14-day trial.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="signup-org-form">
              <div className="space-y-1.5">
                <Label htmlFor="org-name" className="label-caps">Care organisation name</Label>
                <Input
                  id="org-name"
                  required
                  minLength={2}
                  data-testid="signup-org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Sunrise Care Ltd"
                />
                <p className="text-xs text-slate-500">
                  We&apos;ll create a unique workspace slug from this name automatically.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-name" className="label-caps">Your full name</Label>
                  <Input
                    id="admin-name"
                    required
                    data-testid="signup-admin-name"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Priya Patel"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-email" className="label-caps">Work email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    required
                    data-testid="signup-admin-email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="you@yourcompany.co.uk"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-password" className="label-caps">Password (min 6 chars)</Label>
                <Input
                  id="admin-password"
                  type="password"
                  required
                  minLength={6}
                  data-testid="signup-admin-password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="signup-org-submit"
                className="h-11 bg-teal-700 hover:bg-teal-800 rounded-full px-8"
              >
                {busy ? "Creating workspace…" : "Create workspace"}
              </Button>
            </form>

            <p className="text-sm text-slate-600 mt-8">
              Already have a workspace?{" "}
              <Link to="/login" data-testid="link-login" className="text-teal-700 font-semibold hover:underline">
                Sign in
              </Link>
              . Joining an existing workspace?{" "}
              <Link to="/register" data-testid="link-worker-register" className="text-teal-700 font-semibold hover:underline">
                Register as a worker
              </Link>
              .
            </p>
          </div>

          {/* Right: what you get */}
          <div className="lg:col-span-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="label-caps text-slate-500">What&apos;s inside your workspace</div>
              <ul className="mt-4 space-y-4">
                {[
                  {
                    Icon: ListChecks,
                    t: "Compliance checklists",
                    d: "17 standard items + role-specific requirements auto-configured for HCAs, senior carers, nurses, chefs and kitchen assistants.",
                  },
                  {
                    Icon: ShieldCheck,
                    t: "DBS + NMC monitoring",
                    d: "DBS Update Service verification and monthly NMC register checks with a full audit trail.",
                  },
                  {
                    Icon: Users,
                    t: "Team invites",
                    d: "Add reviewers, managers and workers with one click — everyone lands in your workspace only.",
                  },
                ].map((s) => (
                  <li key={s.t} className="flex gap-3">
                    <div className="h-8 w-8 rounded-md bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                      <s.Icon className="h-4 w-4 text-teal-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{s.t}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
